import "./index.css";
import type { FactoryOpts, InputMask } from "imask";
import { XIcon } from "lucide-react";
import type { ChangeEvent, KeyboardEvent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useIMask } from "react-imask";
import { useTranslations } from "../../i18n";
import {
  composeEventHandlers,
  mergeRefs,
  textFieldButtonColor,
  textFieldButtonSize,
} from "../../utils";
import { IconButton } from "../Button/IconButton";
import { Spinner } from "../Spinner";
import { type ISpinnerSize } from "../Spinner/types";
import type { IInputColor, IInputProps, IInputSize } from "./types";
import { clsx } from "clsx";
import { FieldAccessory } from "../FieldAccessory";
import { useMediaQuery } from "../../hooks/useMediaQuery";

export const Input = (props: IInputProps) => {
  const { t } = useTranslations();
  const isMobile = useMediaQuery("(max-width: 639px)");
  const size: IInputSize = props.size || (isMobile ? "lg" : "md");
  const color: IInputColor = props.color || "layer1";
  const onChange = props.onChange;
  const regex = props.regex;
  const internalRef = useRef<HTMLInputElement>(null);

  const hasStartAccessory = !!props.startAccessory;
  const hasEndAccessory = !!(
    props.endAccessory ||
    props.onClear ||
    props.loading
  );

  const rootClass: string = clsx(
    "nuco-input",
    `nuco-input--${size}`,
    `nuco-input--${color}`,
    props.focused && "nuco-input--focused",
    props.invalid && "nuco-input--invalid",
    props.disabled && "nuco-input--disabled",
    props.readOnly && "nuco-input--readOnly",
    props.className,
  );

  const fontSizeMap: Record<IInputSize, string> = {
    sm: "nuco-typography--body",
    md: "nuco-typography--body",
    lg: "nuco-typography--body",
  };

  const inputClass: string = clsx(
    "nuco-input__input",
    fontSizeMap[size],
    hasStartAccessory && "nuco-input__input--withStartAccessory",
  );

  const spinnerSizeMap: Record<IInputSize, ISpinnerSize> = {
    sm: "xs",
    md: "sm",
    lg: "md",
  };

  const spinnerSize: ISpinnerSize =
    props.spinnerProps?.size || spinnerSizeMap[size];

  const IMaskOptions = useMemo(() => {
    if (!props.mask) return null;

    if (typeof props.mask === "string") {
      return { mask: props.mask };
    }

    return props.mask;
  }, [props.mask]);

  const handleMaskAccept = useCallback(
    (value: string, mask: InputMask<FactoryOpts>) => {
      if (!IMaskOptions) return;

      let outputValue: string = value;
      if (IMaskOptions.unmask === true) {
        outputValue = mask.unmaskedValue;
      } else if (IMaskOptions.unmask === "typed") {
        outputValue = mask.typedValue;
      }

      onChange?.(outputValue, undefined);
      IMaskOptions.onAccept?.(value, mask);
    },
    [IMaskOptions, onChange],
  );

  const handleMaskComplete = useCallback(
    (value: string, mask: InputMask<FactoryOpts>) => {
      IMaskOptions?.onComplete?.(value, mask);
    },
    [IMaskOptions],
  );

  const {
    ref: maskRef,
    value: maskValue,
    setValue: setMaskValue,
  } = useIMask<HTMLInputElement>(
    IMaskOptions ? IMaskOptions : { mask: "" },
    IMaskOptions
      ? {
          onAccept: handleMaskAccept,
          onComplete: handleMaskComplete,
          ref: props.ref,
        }
      : undefined,
  );

  const inputRef = mergeRefs(internalRef, maskRef, props.ref);

  useEffect(() => {
    if (
      IMaskOptions &&
      props.value !== undefined &&
      props.value !== maskValue
    ) {
      setMaskValue(String(props.value));
    }
  }, [props.value, IMaskOptions, maskValue, setMaskValue]);

  const nativeProps: IInputProps = useMemo(() => {
    const nativeProps = { ...props };
    delete nativeProps.startAccessory;
    delete nativeProps.endAccessory;
    delete nativeProps.onClear;
    delete nativeProps.loading;
    delete nativeProps.invalid;
    delete nativeProps.spinnerProps;
    delete nativeProps.size;
    delete nativeProps.regex;
    delete nativeProps.onChange;
    delete nativeProps.mask;
    return nativeProps;
  }, [props]);

  const handleClear = useCallback(
    (e: MouseEvent) => {
      if (IMaskOptions) {
        setMaskValue("");
        onChange?.("", undefined);
      }
      props.onClear?.(e);
      internalRef.current?.focus();
    },
    [IMaskOptions, props, setMaskValue, onChange],
  );

  const clearButtonJSX = !props.disabled && props.onClear && (
    <IconButton
      variant="filled"
      size={textFieldButtonSize[size]}
      color={textFieldButtonColor[color]}
      onClick={handleClear}
      content={<XIcon />}
      aria-label={t("ui.input.clearField")}
    />
  );

  const spinnerJSX = !props.disabled && props.loading && (
    <FieldAccessory size={size}>
      <Spinner color="light" size={spinnerSize} {...props.spinnerProps} />
    </FieldAccessory>
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      if (IMaskOptions) return;

      let value = e.currentTarget.value;

      if (regex) {
        value = value
          .split("")
          .filter((char) => regex.test(char))
          .join("");
      }

      if (onChange) {
        onChange(value, e);
      }
    },
    [onChange, regex, IMaskOptions],
  );

  const value: string = useMemo(() => {
    return IMaskOptions ? maskValue : props.value?.toString() || "";
  }, [props.value, IMaskOptions, maskValue]);

  const internalKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!regex) return;

      const isValidCharacter = regex.test(e.key);
      const navigationKeys = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Tab",
        "Enter",
      ];

      if (!isValidCharacter && !navigationKeys.includes(e.key)) {
        e.preventDefault();
      }
    },
    [regex],
  );

  const onKeyDownComposed = useMemo(
    () => composeEventHandlers(props.onKeyDown as any, internalKeyDown as any),
    [props.onKeyDown, internalKeyDown],
  );

  return (
    <div className={rootClass}>
      {hasStartAccessory && (
        <div className="nuco-input__accessory nuco-input__startAccessory">
          {props.startAccessory}
        </div>
      )}
      <input
        {...nativeProps}
        ref={inputRef}
        size={undefined}
        className={inputClass}
        onChange={handleChange}
        onKeyDown={onKeyDownComposed}
        value={IMaskOptions ? undefined : props.value}
        aria-invalid={props.invalid}
      />
      {hasEndAccessory && (
        <div className="nuco-input__accessory nuco-input__endAccessory">
          {value.length > 0 && clearButtonJSX}
          {!spinnerJSX && props.endAccessory && props.endAccessory}
          {spinnerJSX}
        </div>
      )}
    </div>
  );
};
